import { useState } from "react";
import { Link, NavLink, Outlet } from "react-router-dom";
import { Menu, X, Phone, Mail, ShieldCheck } from "lucide-react";
import { T } from "@/lib/testIds";

const LOGO_URL = "/logo.png";

const NAV = [
  { to: "/", label: "Home", tid: T.nav.home },
  { to: "/services", label: "Services", tid: T.nav.services },
  { to: "/about", label: "About Us", tid: T.nav.about },
  { to: "/patients", label: "For Patients", tid: T.nav.patients },
  { to: "/careers", label: "Careers", tid: T.nav.careers },
  { to: "/contact", label: "Contact", tid: T.nav.contact },
];

function Logo() {
  return (
    <Link to="/" className="flex items-center" data-testid="brand-logo">
      <img
        src={LOGO_URL}
        alt="Moreno Quality Transportation — Non-Emergency Medical Transportation"
        className="h-12 sm:h-14 w-auto object-contain"
      />
    </Link>
  );
}

function Header() {
  const [open, setOpen] = useState(false);
  return (
    <header className="sticky top-0 z-40 backdrop-blur-xl bg-white/85 border-b border-slate-200">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 h-20 flex items-center justify-between">
        <Logo />
        <nav className="hidden lg:flex items-center gap-1">
          {NAV.map((n) => (
            <NavLink
              key={n.to}
              to={n.to}
              data-testid={n.tid}
              className={({ isActive }) =>
                `px-4 py-2 text-sm font-semibold rounded-full transition-colors duration-200 ${
                  isActive ? "text-[#1D4ED8] bg-blue-50" : "text-slate-700 hover:text-[#0A192F] hover:bg-slate-100"
                }`
              }
              end={n.to === "/"}
            >
              {n.label}
            </NavLink>
          ))}
        </nav>
        <button
          className="lg:hidden p-2 rounded-lg hover:bg-slate-100"
          onClick={() => setOpen((o) => !o)}
          data-testid={T.nav.menu}
          aria-label="Toggle menu"
        >
          {open ? <X className="w-6 h-6" /> : <Menu className="w-6 h-6" />}
        </button>
      </div>
      {open && (
        <div className="lg:hidden border-t border-slate-200 bg-white">
          <div className="px-4 py-3 flex flex-col gap-1">
            {NAV.map((n) => (
              <NavLink
                key={n.to}
                to={n.to}
                data-testid={`${n.tid}-m`}
                onClick={() => setOpen(false)}
                className={({ isActive }) =>
                  `px-4 py-3 rounded-lg text-sm font-semibold ${
                    isActive ? "bg-blue-50 text-[#1D4ED8]" : "text-slate-700 hover:bg-slate-100"
                  }`
                }
                end={n.to === "/"}
              >
                {n.label}
              </NavLink>
            ))}
          </div>
        </div>
      )}
    </header>
  );
}

const PHONE = "(813) 524-8367";
const EMAIL = "Morenoqualityservice2026@gmail.com";

function Footer() {
  return (
    <footer className="bg-[#0A192F] text-slate-300 mt-24">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-16 grid grid-cols-1 md:grid-cols-4 gap-10">
        <div>
          <div className="inline-flex bg-white/95 rounded-xl px-3 py-2 mb-4 shadow-sm">
            <img src={LOGO_URL} alt="Moreno Quality Transportation" className="h-12 w-auto object-contain" />
          </div>
          <p className="text-sm text-slate-400 leading-relaxed">Safe, reliable, and compassionate non-emergency medical transportation.</p>
        </div>
        <div>
          <h4 className="text-white font-semibold mb-4 text-sm uppercase tracking-wider">Quick Links</h4>
          <ul className="space-y-2 text-sm">
            {NAV.map((n) => (
              <li key={n.to}><Link to={n.to} className="hover:text-white transition-colors">{n.label}</Link></li>
            ))}
          </ul>
        </div>
        <div>
          <h4 className="text-white font-semibold mb-4 text-sm uppercase tracking-wider">Services</h4>
          <ul className="space-y-2 text-sm text-slate-400">
            <li>Medical Appointments</li>
            <li>Wheelchair Transportation</li>
            <li>Hospital Transportation</li>
            <li>Senior Transportation</li>
            <li>Private Transportation</li>
          </ul>
        </div>
        <div>
          <h4 className="text-white font-semibold mb-4 text-sm uppercase tracking-wider">Contact</h4>
          <ul className="space-y-3 text-sm">
            <li className="flex items-center gap-2"><Phone className="w-4 h-4 text-emerald-400" /><a href={`tel:${PHONE.replace(/\D/g, "")}`} className="hover:text-white">{PHONE}</a></li>
            <li className="flex items-center gap-2"><Mail className="w-4 h-4 text-emerald-400" /><a href={`mailto:${EMAIL}`} className="hover:text-white break-all">{EMAIL}</a></li>
            <li className="text-slate-400">Service Area: Florida</li>
          </ul>
        </div>
      </div>
      <div className="border-t border-white/10">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-5 flex flex-col md:flex-row items-center justify-between gap-3 text-xs text-slate-400">
          <div>© 2026 Moreno Quality Transportation. All Rights Reserved.</div>
          <div className="flex items-center gap-6">
            <Link to="/patients" className="hover:text-white">Privacy Policy</Link>
            <Link to="/patients" className="hover:text-white">Terms of Use</Link>
          </div>
        </div>
      </div>
    </footer>
  );
}

export default function Layout() {
  return (
    <div className="min-h-screen flex flex-col bg-white">
      <Header />
      <main className="flex-1"><Outlet /></main>
      <Footer />
    </div>
  );
}

export { PHONE, EMAIL };
